import { Component } from "@theme/component";

export class PickACardComponent extends Component {
  /** @type {HTMLElement | null} */
  container = null;
  /** @type {HTMLElement | null} */
  cardModal = null;
  /** @type {HTMLElement | null} */
  cardsGrid = null;
  /** @type {HTMLInputElement | null} */
  cardInput = null;
  /** @type {HTMLElement | null} */
  previewArea = null;
  /** @type {HTMLImageElement | null} */
  previewImage = null;
  /** @type {HTMLElement | null} */
  previewName = null;
  /** @type {HTMLButtonElement | null} */
  removeBtn = null;
  /** @type {HTMLElement | null} */
  cardBtnLabel = null;
  /** @type {HTMLTextAreaElement | null} */
  messageTextarea = null;
  /** @type {HTMLElement | null} */
  charCount = null;
  /** @type {HTMLInputElement | null} */
  blankCheckbox = null;
  /** @type {HTMLInputElement | null} */
  noNoteCheckbox = null;
  /** @type {NodeListOf<HTMLElement> | null} */
  openModalBtns = null;
  /** @type {any | null} */
  selectedCard = null;

  constructor() {
    super();
    this.selectedCard = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.init();
    this.setupMorphHandling();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanup();
  }

  init() {
    this.cacheElements();
    this.bindEvents();
    this.initializeCheckboxStates();
  }

  cacheElements() {
    // Get the parent container that holds all the elements
    this.container = this.closest("[data-card-form]") || this;

    // Modal and grid elements
    this.cardModal = this.container.querySelector(
      'dialog-component[ref="cardSelectionModal"]'
    );
    this.cardsGrid = this.container.querySelector("[data-cards-grid]");
    this.cardInput = this.container.querySelector("[data-card-input]");

    // Preview elements
    this.previewArea = this.container.querySelector("[data-card-preview]");
    this.previewImage = this.container.querySelector("[data-preview-image]");
    this.previewName = this.container.querySelector("[data-preview-name]");
    this.removeBtn = this.container.querySelector("[data-remove-card]");
    this.cardBtnLabel = this.container.querySelector("[data-card-btn-label]");

    // Form elements
    this.messageTextarea = this.container.querySelector("[data-card-message]");
    this.charCount = this.container.querySelector("[data-char-count]");
    this.blankCheckbox = this.container.querySelector("[data-card-blank]");
    this.noNoteCheckbox = this.container.querySelector("[data-card-no-note]");
    this.openModalBtns = this.container.querySelectorAll(
      "[data-open-card-modal]"
    );
  }

  bindEvents() {
    // Use event delegation on the container for better performance and morph-resistance
    if (this.container) {
      this.container.addEventListener("click", this.handleClick.bind(this));
      this.container.addEventListener(
        "mouseenter",
        this.handleMouseEnter.bind(this),
        true
      );
      this.container.addEventListener(
        "mouseleave",
        this.handleMouseLeave.bind(this),
        true
      );
      this.container.addEventListener("input", this.handleInput.bind(this));
      this.container.addEventListener("change", this.handleChange.bind(this));
    }
  }

  cleanup() {
    if (this.container) {
      this.container.removeEventListener("click", this.handleClick.bind(this));
      this.container.removeEventListener(
        "mouseenter",
        this.handleMouseEnter.bind(this),
        true
      );
      this.container.removeEventListener(
        "mouseleave",
        this.handleMouseLeave.bind(this),
        true
      );
      this.container.removeEventListener("input", this.handleInput.bind(this));
      this.container.removeEventListener(
        "change",
        this.handleChange.bind(this)
      );
    }
  }

  /**
   * @param {Event} e
   */
  handleClick(e) {
    if (!e.target) return;
    const target = /** @type {Element} */ (e.target);

    // Handle card selection
    const selectBtn = target.closest("[data-select-card]");
    if (selectBtn) {
      this.handleCardSelection(e);
      return;
    }

    // Handle remove card
    const removeBtn = target.closest("[data-remove-card]");
    if (removeBtn) {
      this.handleRemoveCard(e);
      return;
    }

    // Handle open modal
    const openModalBtn = target.closest("[data-open-card-modal]");
    if (openModalBtn) {
      this.handleOpenModal(e);
      return;
    }
  }

  /**
   * @param {Event} e
   */
  handleMouseEnter(e) {
    if (!e.target) return;
    const target = /** @type {Element} */ (e.target);
    const cardItem = target.closest("[data-card-item]");
    if (cardItem) {
      cardItem.classList.add("hovered");
    }
  }

  /**
   * @param {Event} e
   */
  handleMouseLeave(e) {
    if (!e.target) return;
    const target = /** @type {Element} */ (e.target);
    const cardItem = target.closest("[data-card-item]");
    if (cardItem) {
      cardItem.classList.remove("hovered");
    }
  }

  /**
   * @param {Event} e
   */
  handleInput(e) {
    if (!e.target) return;
    const target = /** @type {Element} */ (e.target);
    // Handle message character counter
    if (target.matches("[data-card-message]")) {
      this.handleMessageInput(e);
    }
  }

  /**
   * @param {Event} e
   */
  handleChange(e) {
    if (!e.target) return;
    const target = /** @type {Element} */ (e.target);
    // Handle checkbox changes
    if (target.matches("[data-card-blank], [data-card-no-note]")) {
      this.updateCheckboxStates();
    }
  }

  /**
   * @param {Event} e
   */
  handleCardSelection(e) {
    if (!e.target) return;
    const target = /** @type {Element} */ (e.target);
    const selectBtn = target.closest("[data-select-card]");
    if (!selectBtn) return;

    const cardItem = selectBtn.closest("[data-card-item]");
    if (!cardItem) return;

    // Remove previous selection
    if (this.cardsGrid) {
      this.cardsGrid.querySelectorAll("[data-card-item]").forEach(
        /** @param {Element} item */ (item) => {
          item.classList.remove("selected");
        }
      );
    }

    // Mark as selected
    cardItem.classList.add("selected");

    // Get card data
    const productId = cardItem.getAttribute("data-product-id");
    const variantId = cardItem.getAttribute("data-variant-id");
    const cardImage = cardItem.querySelector("img");
    const cardTitle = cardItem.querySelector(".card-title");

    this.selectedCard = {
      id: productId,
      variantId: variantId,
      image: cardImage ? cardImage.src : null,
      title: cardTitle ? cardTitle.textContent : `Card ${productId}`,
      alt: cardImage ? cardImage.alt : "",
    };

    // Update preview
    this.updateCardPreview();

    // Close modal
    if (this.cardModal && "closeDialog" in this.cardModal) {
      setTimeout(() => {
        if (this.cardModal && "closeDialog" in this.cardModal) {
          /** @type {any} */ (this.cardModal).closeDialog();
        }
      }, 300);
    }

    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent("card-selected", {
        detail: this.selectedCard,
        bubbles: true,
      })
    );
  }

  updateCardPreview() {
    if (!this.selectedCard) return;

    if (this.previewImage && this.selectedCard.image) {
      this.previewImage.src = this.selectedCard.image;
      this.previewImage.alt = this.selectedCard.alt;
    }

    if (this.previewName) {
      this.previewName.textContent = this.selectedCard.title;
    }

    if (this.previewArea) {
      this.previewArea.style.display = "block";
    }

    if (this.cardBtnLabel) {
      this.cardBtnLabel.textContent = "Change Card";
    }

    // Update hidden input for form submission
    if (this.cardInput) {
      this.cardInput.value = JSON.stringify({
        id: this.selectedCard.id,
        variantId: this.selectedCard.variantId,
        title: this.selectedCard.title,
        image: this.selectedCard.image,
      });
    }
  }

  /**
   * @param {Event} e
   */
  handleRemoveCard(e) {
    e.stopPropagation();

    this.selectedCard = null;

    if (this.previewArea) this.previewArea.style.display = "none";
    if (this.cardInput) this.cardInput.value = "";
    if (this.cardBtnLabel) this.cardBtnLabel.textContent = "Pick a Card";

    // Remove selection from grid
    if (this.cardsGrid) {
      this.cardsGrid.querySelectorAll("[data-card-item]").forEach(
        /** @param {Element} item */ (item) => {
          item.classList.remove("selected");
        }
      );
    }

    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent("card-removed", {
        bubbles: true,
      })
    );
  }

  /**
   * @param {Event} e
   */
  handleOpenModal(e) {
    if (this.cardModal && "showDialog" in this.cardModal) {
      /** @type {any} */ (this.cardModal).showDialog();
    }
  }

  /**
   * @param {Event} e
   */
  handleMessageInput(e) {
    if (!this.charCount || !e.target) return;
    const target = /** @type {HTMLTextAreaElement} */ (e.target);

    const length = target.value.length;
    this.charCount.textContent = length.toString();

    const parentElement = this.charCount.parentElement;
    if (parentElement) {
      if (length >= 160) {
        parentElement.classList.add("limit-reached");
      } else {
        parentElement.classList.remove("limit-reached");
      }
    }
  }

  initializeCheckboxStates() {
    if (this.blankCheckbox && this.noNoteCheckbox) {
      this.updateCheckboxStates();
    }
  }

  updateCheckboxStates() {
    if (!this.blankCheckbox || !this.noNoteCheckbox) return;

    if (this.blankCheckbox.checked) {
      // If blank checkbox is checked, disable and uncheck the other
      this.noNoteCheckbox.checked = false;
      this.noNoteCheckbox.disabled = true;
      const label = this.noNoteCheckbox.closest(".checkbox-label");
      if (label) /** @type {HTMLElement} */ (label).style.opacity = "0.5";
    } else if (this.noNoteCheckbox.checked) {
      // If no-note checkbox is checked, disable and uncheck the other
      this.blankCheckbox.checked = false;
      this.blankCheckbox.disabled = true;
      const label = this.blankCheckbox.closest(".checkbox-label");
      if (label) /** @type {HTMLElement} */ (label).style.opacity = "0.5";
    } else {
      // If both are unchecked, enable both
      this.blankCheckbox.disabled = false;
      this.noNoteCheckbox.disabled = false;
      const blankLabel = this.blankCheckbox.closest(".checkbox-label");
      const noNoteLabel = this.noNoteCheckbox.closest(".checkbox-label");
      if (blankLabel)
        /** @type {HTMLElement} */ (blankLabel).style.opacity = "1";
      if (noNoteLabel)
        /** @type {HTMLElement} */ (noNoteLabel).style.opacity = "1";
    }
  }

  // Public API methods
  getSelectedCard() {
    return this.selectedCard;
  }

  clearSelection() {
    const mockEvent = /** @type {Event} */ ({
      stopPropagation: () => {},
    });
    this.handleRemoveCard(mockEvent);
  }

  /**
   * @param {any} cardData
   */
  setCard(cardData) {
    this.selectedCard = cardData;
    this.updateCardPreview();
  }

  // Handle content updates (for when content is morphed)
  handleContentUpdate() {
    this.cacheElements();
    this.initializeCheckboxStates();
  }

  // Listen for modal content updates (for morph compatibility)
  setupMorphHandling() {
    // Listen for content updates that might affect this component
    document.addEventListener("modal-content-updated", (e) => {
      const customEvent = /** @type {CustomEvent} */ (e);
      if (
        customEvent.detail &&
        customEvent.detail.container &&
        customEvent.detail.container.contains(this)
      ) {
        this.handleContentUpdate();
      }
    });
  }
}

if (!customElements.get("pick-a-card-component")) {
  customElements.define("pick-a-card-component", PickACardComponent);
}
